/**
 * 首屏弹窗调度器：管「进页面弹哪个、谁先谁后」。本文件只做决策，渲染在 index.vue。
 *
 * 一共 3 个弹窗，优先级从高到低：签到弹窗 > 后端弹窗 > 任务弹窗。
 *
 * 规则一句话讲清：
 *   · 链接带 anchorModule=签到 → 签到弹窗先弹（不管今天弹没弹过），后端弹窗排到它后面。
 *   · 没带 → 后端下发什么就弹什么（后端下发的是签到窗才弹签到，且当天只弹一次）。
 *   · 签到弹窗关掉后：有排队的后端弹窗就接着弹，没有就弹任务弹窗（二选一）。
 *
 * 宿主只在 3 个时机叫一声调度器：
 *   getPopupWindow 回来  → onBackendPopupReady()
 *   签到数据回来        → onSignDataReady()
 *   签到弹窗关闭        → onAllSignPopupClosed()
 *
 * 想加个新弹窗？看是哪种：
 *   · 普通后端弹窗（一张图 / 一个组件）：不用动本文件，去 consts.js 登记
 *     —— 图片弹窗后端给 styleType=CENTER；自定义组件在 WELFARE_CENTER_POPUP_WINDOW_COMPONENT 配 key→组件。
 *   · 像签到弹窗这种要深链/原生桥/专属数据的：照着签到弹窗在下面 onXxx 里加判断。
 */

import {
  POPUP_WINDOW_KEY,
  POPUP_STYLE_TYPE,
  WELFARE_CENTER_POPUP_WINDOW_COMPONENT,
  WIDGET_TEMPLATE_ID,
  WIDGET_SOURCE,
} from '../consts';
import { addWidget } from '../common/bridge';

// 弹窗疲劳度控制 localStorage key 前缀（按用户隔离，当天最多弹一次）
const SIGN_POPUP_SHOWN_TODAY_PREFIX = 'SIGN_POPUP_SHOWN_TODAY';
const TASK_POPUP_SHOWN_TODAY_PREFIX = 'TASK_POPUP_SHOWN_TODAY';

// 今日是否已展示过（localStorage 异常时降级为「未展示」，允许展示）
function isShownToday(key) {
  try {
    return localStorage.getItem(key) === new Date().toDateString();
  } catch (e) {
    return false;
  }
}

function markShownToday(key) {
  try {
    localStorage.setItem(key, new Date().toDateString());
  } catch (e) {
    // localStorage 写入失败不阻断流程
  }
}

// ---------------------------------------------------------------------------
// 纯工具函数（无副作用，可单测）
// ---------------------------------------------------------------------------

/**
 * 锚定 key 是否为首屏弹窗 key（命中则走弹窗直达而非模块滚动）。
 * 注：取自首屏弹窗枚举全集，但当前仅支持签到弹窗（checkin_window）深链直达。
 */
export function isPopupAnchorKey(key) {
  return !!key && Object.values(POPUP_WINDOW_KEY).includes(key);
}

// 链接锚定 key 是否指向签到弹窗
export function isAnchorSignPopup(anchorModule) {
  return anchorModule === POPUP_WINDOW_KEY.checkin_window;
}

// 解析后端首屏弹窗接口数据为统一结构 { key, styleType, data }；无数据返回 null
export function parseBackendPopup(list) {
  if (!list || list.length === 0) return null;
  const {
    popupWindowKey,
    popupWindowStyleType = POPUP_STYLE_TYPE.CUSTOM,
    popupWindowData = {},
  } = list[0] || {};
  return {
    key: popupWindowKey || POPUP_WINDOW_KEY.checkin_window,
    // 仅居中通用图片弹窗保留 CENTER，其余按个性化弹窗处理
    styleType: popupWindowStyleType === POPUP_STYLE_TYPE.CENTER
      ? POPUP_STYLE_TYPE.CENTER
      : POPUP_STYLE_TYPE.CUSTOM,
    data: popupWindowData,
  };
}

/**
 * 首屏弹窗调度器。只负责「决定弹什么、什么顺序」，渲染交给宿主注入的 effects。
 * 用法见文件顶部流程图，宿主只需在 3 个时机调用对应的 onXxx 方法。
 */
export default class FirstScreenPopupScheduler {
  /**
   * @param {Object} effects 宿主注入的副作用与查询
   * @param {() => string} effects.getUserId          获取用户 ID（疲劳度 key）
   * @param {() => string} effects.getAnchorModule    获取链接锚定 key
   * @param {() => {key:string, styleType:number}} effects.getBackendPopup 获取后端首屏弹窗配置
   * @param {() => void} effects.openVuePopup          展示 Vue 首屏弹窗（置 visible）
   * @param {() => void} effects.showSignPopup         展示签到弹窗（App/H5 由宿主实现）
   * @param {() => void} effects.openTaskPopup         展示任务弹窗
   */
  constructor(effects) {
    this.effects = effects;
    // 后端首屏弹窗是否因签到弹窗优先而延后（待签到弹窗关闭后补弹）
    this.pendingBackendPopup = false;
    // 链接签到弹窗本次访问是否已弹出（绕过疲劳度后用于防止签到刷新导致重复弹出）
    this.linkSignPopupShown = false;
  }

  get signFatigueKey() {
    return `${SIGN_POPUP_SHOWN_TODAY_PREFIX}_${this.effects.getUserId()}`;
  }

  get taskFatigueKey() {
    return `${TASK_POPUP_SHOWN_TODAY_PREFIX}_${this.effects.getUserId()}`;
  }

  // =========================================================================
  // 时机入口 1/3：后端首屏弹窗配置就绪
  // =========================================================================
  onBackendPopupReady() {
    // 链接要弹签到窗 → 后端弹窗让位延后；否则立即展示
    if (isAnchorSignPopup(this.effects.getAnchorModule())) {
      this.pendingBackendPopup = true;
      return;
    }
    this.presentBackendPopup();
  }

  // =========================================================================
  // 时机入口 2/3：签到数据就绪 → 是否弹签到弹窗
  // =========================================================================
  /**
   * @param {Object} ctx
   * @param {boolean} ctx.hasSignData  是否有签到计划数据
   * @param {boolean} ctx.didSignToday 今日是否已签到
   */
  onSignDataReady({ hasSignData, didSignToday }) {
    // ① 链接深链：强弹签到窗，不受疲劳度 / didSignToday 约束，本次仅一次
    if (isAnchorSignPopup(this.effects.getAnchorModule())) {
      if (this.linkSignPopupShown) return;
      this.linkSignPopupShown = true;
      if (hasSignData) {
        this.effects.showSignPopup();
      } else {
        // 没签到数据弹不出签到窗 → 立刻补弹此前让位的后端弹窗，避免其丢失
        this.flushPendingBackendPopup();
      }
      return;
    }

    // ② 非深链：后端下发的就是签到窗，且当天没弹过 → 弹（疲劳度控制）
    const { key } = this.effects.getBackendPopup() || {};
    if (
      !didSignToday
      && hasSignData
      && key === POPUP_WINDOW_KEY.checkin_window
      && !isShownToday(this.signFatigueKey)
    ) {
      this.effects.showSignPopup();
      markShownToday(this.signFatigueKey);
    }
  }

  // =========================================================================
  // 时机入口 3/3：签到弹窗链关闭 → 补弹后端弹窗 或 任务弹窗（二选一）
  // =========================================================================
  onAllSignPopupClosed() {
    if (this.flushPendingBackendPopup()) return;
    if (!isShownToday(this.taskFatigueKey)) {
      this.effects.openTaskPopup();
      markShownToday(this.taskFatigueKey);
    }
  }

  // =========================================================================
  // 后端首屏弹窗的展示与让位补弹
  // =========================================================================
  /**
   * 展示后端首屏弹窗。新增一种「后端弹窗形态」时在这里加分支。
   */
  presentBackendPopup() {
    const { key, styleType } = this.effects.getBackendPopup() || {};
    if (!key) return;
    // 形态一：小组件弹窗 → 调桥拉起半屏（无 Vue 弹窗）
    if (key === POPUP_WINDOW_KEY.component_popup) {
      addWidget({ templateId: WIDGET_TEMPLATE_ID, source: WIDGET_SOURCE });
      return;
    }
    // 形态二/三：居中图片弹窗 或 已注册组件的个性化弹窗 → 置 visible 交给 Vue 渲染
    if (styleType === POPUP_STYLE_TYPE.CENTER || WELFARE_CENTER_POPUP_WINDOW_COMPONENT[key]) {
      this.effects.openVuePopup();
    }
  }

  // 补弹此前让位的后端首屏弹窗，返回是否有补弹动作
  flushPendingBackendPopup() {
    if (!this.pendingBackendPopup) return false;
    this.pendingBackendPopup = false;
    this.presentBackendPopup();
    return true;
  }
}
